/**
 * DOCX welding-question parser (pure logic, no DOM/Node bindings).
 *
 * Browser File loader lives in ./docxParserBrowser.ts.
 *
 * Supports two source layouts:
 *  1) Spec format with Q1: / A./B./C./D. / Correct: / Explanation / Category
 *     / Difficulty / Code Reference markers.
 *  2) Free-form: question line, then four lines "A) ..." "B) ..." etc.
 *     No correct answer in source — flagged needs-review for the manager.
 *
 * True/False items are skipped — schema is 4-option multiple choice.
 */

import type { ParsedQuestionDraft, ParsedQuestionDocResult } from "@/types/questions";
import type { Difficulty, OptionLetter } from "@/types/database";

const TITLE_RE = /^\s*Title\s*[:\-]\s*(.+)$/i;
const QNUM_RE = /^\s*Q\s*(\d+)\s*[:\.\)]\s*(.+)$/i;
const OPT_RE = /^\s*([A-D])\s*[\.\)\:]\s*(.+?)\.?\s*$/i;
const CORRECT_RE = /^\s*Correct\s*[:\-]\s*([A-D])\s*$/i;
const EXPL_RE = /^\s*Explanation\s*[:\-]\s*(.+)$/i;
const CAT_RE = /^\s*Category\s*[:\-]\s*(.+)$/i;
const DIFF_RE = /^\s*Difficulty\s*[:\-]\s*(.+)$/i;
const CODE_RE = /^\s*Code\s*Reference\s*[:\-]\s*(.+)$/i;

const CATEGORY_HINTS: Array<[string, string[]]> = [
  ["Safety", ["ppe", "safety", "shock", "fume", "noise", "dosimeter", "lockout", "fall", "fire", "hazard", "respirator", "exposure", "radiation", "ventilation", "ozone"]],
  ["Oxy-Fuel Cutting", ["oxy-fuel", "oxyfuel", "ofc", "torch", "acetylene", "regulator", "backfire", "cutting"]],
  ["SMAW", ["smaw", "stick", "electrode", "exx", "low-hydrogen"]],
  ["GMAW", ["gmaw", "mig", "shielding gas", "spray transfer", "short-circuit"]],
  ["Welding Symbols", ["welding symbol", "nde", "fillet weld gauge", "back weld", "flare", "groove"]],
  ["Inspection", ["inspect", "tolerance", "warpage", "camber", "distortion", "blister", "scab"]],
  ["Measurement", ["micrometer", "tape", "spirit level", "square", "gauge", "tension"]],
  ["WPS / Procedure", ["wps", "procedure specification"]],
];

// Tokenize on anything that isn't a letter or digit so "fire" doesn't hit
// inside "backfire" and mis-categorize an oxy-fuel question as Safety.
const TOKEN_SPLIT_RE = /[^a-zA-Z0-9]+/;

function guessCategory(question: string): string {
  const q = question.toLowerCase();
  const tokens = new Set(q.split(TOKEN_SPLIT_RE).filter((t) => t.length > 0));
  for (const [label, keys] of CATEGORY_HINTS) {
    for (const k of keys) {
      if (k.includes(" ") || k.includes("-")) {
        if (q.includes(k)) return label;
      } else if (tokens.has(k)) {
        return label;
      }
    }
  }
  return "General";
}

function normalizeDifficulty(raw: string | null | undefined): Difficulty {
  if (!raw) return "beginner";
  const v = raw.trim().toLowerCase();
  if (v.startsWith("inter")) return "intermediate";
  if (v.startsWith("adv")) return "advanced";
  return "beginner";
}

export function parseQuestionsFromText(rawText: string): ParsedQuestionDocResult {
  const lines = rawText
    .split(/\r?\n/)
    .map((l) => l.replace(/ /g, " ").trim())
    .filter((l) => l.length > 0);

  let title = "Imported Question Bank";
  const out: ParsedQuestionDraft[] = [];

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    const mTitle = TITLE_RE.exec(line);
    if (mTitle) { title = mTitle[1].trim(); i++; continue; }

    const mQ = QNUM_RE.exec(line);
    if (mQ) {
      const draft = newDraft(out.length + 1, mQ[2].trim());
      i++;
      while (i < lines.length) {
        const l = lines[i];
        const mo = OPT_RE.exec(l);
        if (mo) { assignOption(draft, mo[1].toUpperCase() as OptionLetter, mo[2].trim()); i++; continue; }
        const mc = CORRECT_RE.exec(l); if (mc) { draft.correct_option = mc[1].toUpperCase() as OptionLetter; i++; continue; }
        const me = EXPL_RE.exec(l); if (me) { draft.explanation = me[1].trim(); i++; continue; }
        const mca = CAT_RE.exec(l); if (mca) { draft.category = mca[1].trim(); i++; continue; }
        const md = DIFF_RE.exec(l); if (md) { draft.difficulty = normalizeDifficulty(md[1]); i++; continue; }
        const mcr = CODE_RE.exec(l); if (mcr) { draft.code_reference = mcr[1].trim(); i++; continue; }
        break;
      }
      finalizeDraft(draft);
      out.push(draft);
      continue;
    }

    if (!OPT_RE.test(line) && !/^(true|false)$/i.test(line)) {
      const draft = newDraft(out.length + 1, line.replace(/\.\s*$/, ""));
      let j = i + 1;
      let consumed = 0;
      while (j < lines.length && consumed < 6) {
        const mo = OPT_RE.exec(lines[j]);
        if (mo) { assignOption(draft, mo[1].toUpperCase() as OptionLetter, mo[2].trim()); j++; consumed++; continue; }
        break;
      }
      const optionCount = [draft.option_a, draft.option_b, draft.option_c, draft.option_d]
        .filter((v) => v && v.length > 0).length;
      if (optionCount >= 4) {
        // Look ahead for optional metadata lines: Correct, Explanation, Category, Difficulty, Code Reference
        while (j < lines.length) {
          const l = lines[j];
          const mc = CORRECT_RE.exec(l); if (mc) { draft.correct_option = mc[1].toUpperCase() as OptionLetter; j++; continue; }
          const me = EXPL_RE.exec(l);    if (me) { draft.explanation = me[1].trim(); j++; continue; }
          const mca = CAT_RE.exec(l);    if (mca) { draft.category = mca[1].trim(); j++; continue; }
          const md = DIFF_RE.exec(l);    if (md) { draft.difficulty = normalizeDifficulty(md[1]); j++; continue; }
          const mcr = CODE_RE.exec(l);   if (mcr) { draft.code_reference = mcr[1].trim(); j++; continue; }
          break;
        }
        draft.category ??= guessCategory(draft.question_text);
        finalizeDraft(draft);
        out.push(draft);
        i = j;
        continue;
      }
      if (j + 1 < lines.length && /^true$/i.test(lines[j]) && /^false$/i.test(lines[j + 1])) {
        i = j + 2;
        continue;
      }
      i++;
      continue;
    }
    i++;
  }

  const valid_count = out.filter((q) => q.valid).length;
  return { title, questions: out, total: out.length, valid_count, issue_count: out.length - valid_count };
}

function newDraft(position: number, question_text: string): ParsedQuestionDraft {
  return {
    position, question_text,
    option_a: "", option_b: "", option_c: "", option_d: "",
    correct_option: null, explanation: null, category: null,
    difficulty: "beginner", code_reference: null,
    valid: false, issues: [],
  };
}

function assignOption(d: ParsedQuestionDraft, letter: OptionLetter, value: string): void {
  if (letter === "A") d.option_a = value;
  else if (letter === "B") d.option_b = value;
  else if (letter === "C") d.option_c = value;
  else d.option_d = value;
}

function finalizeDraft(d: ParsedQuestionDraft): void {
  const issues: string[] = [];
  if (!d.question_text) issues.push("Missing question text");
  if (!d.option_a) issues.push("Missing option A");
  if (!d.option_b) issues.push("Missing option B");
  if (!d.option_c) issues.push("Missing option C");
  if (!d.option_d) issues.push("Missing option D");
  if (!d.correct_option) issues.push("Missing correct answer (set before publishing)");
  d.issues = issues;
  d.valid = issues.length === 0;
}
